import { useQuery } from "@tanstack/react-query"
import { Link } from "react-router-dom"

import { AdminPageHeader } from "@/components/AdminPageHeader"
import { DateToolbar, useKitchenDate } from "@/components/DateToolbar"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { api } from "@/lib/client"
import { formatAddress, formatDateLabel, formatModifiers, windowLabel } from "@/lib/format"
import { queryKeys } from "@/lib/queryKeys"
import { statusBadgeClass } from "@/lib/table"

export function KitchenPackingPage() {
  const [date, setDate] = useKitchenDate()
  const packing = useQuery({
    queryFn: () => api.kitchenPacking(date),
    queryKey: queryKeys.kitchenPacking(date),
  })
  const windows = packing.data?.windows ?? []

  return (
    <div>
      <AdminPageHeader
        description={`${formatDateLabel(date)} · pack by window, then stop`}
        title="Packing"
      >
        <DateToolbar date={date} onChange={setDate} onPrint={() => window.print()} />
      </AdminPageHeader>
      {packing.isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : windows.length === 0 ? (
        <p className="text-sm text-muted-foreground">No packing stops for this date.</p>
      ) : (
        <div className="space-y-6 print-root">
          {windows.map((group) => (
            <section className="space-y-3" key={group.window}>
              <h2 className="text-sm font-semibold tracking-wider text-muted-foreground uppercase">
                {windowLabel(group.window)} · {group.stops.length} stops
              </h2>
              <div className="grid gap-3">
                {group.stops.map((stop) => (
                  <Card key={stop.deliveryId}>
                    <CardHeader className="flex flex-row items-start justify-between gap-4 pb-3">
                      <div>
                        <CardTitle className="text-base">
                          <Link className="hover:underline" to={`/orders/${stop.orderId}`}>
                            {stop.customer.name || stop.orderName}
                          </Link>
                        </CardTitle>
                        <p className="mt-1 text-sm text-muted-foreground">{formatAddress(stop.address)}</p>
                      </div>
                      <span className={statusBadgeClass(stop.state)}>{stop.state.replaceAll("_", " ")}</span>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {stop.allergens.length > 0 ? (
                        <p className="text-xs font-medium text-amber-300">
                          Allergens: {stop.allergens.join(", ")}
                        </p>
                      ) : null}
                      <ul className="space-y-1 text-sm">
                        {stop.lines.map((line) => (
                          <li key={line.id}>
                            <span className="font-medium">{line.quantity}×</span> {line.name}
                            {line.mealTime ? (
                              <span className="text-muted-foreground"> · {line.mealTime}</span>
                            ) : null}
                            {formatModifiers(line.modifiers) ? (
                              <span className="text-muted-foreground">
                                {" "}
                                · {formatModifiers(line.modifiers)}
                              </span>
                            ) : null}
                          </li>
                        ))}
                      </ul>
                      <Link
                        className="inline-block text-xs text-muted-foreground hover:underline"
                        to={`/orders/${stop.orderId}/work-order/${date}`}
                      >
                        Work order
                      </Link>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  )
}
